import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Icon } from '@iconify/react';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PersonIcon from '@mui/icons-material/Person';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HistoryToggleOffOutlinedIcon from '@mui/icons-material/HistoryToggleOffOutlined';

import {
    Grid,
    Card,
    CardContent,
    Typography,
    Divider,
    Box,
    Tooltip,
    Button,
    Chip,
    TextField,
    Badge
} from '@mui/material';

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const ServiceCards = ({ serviceData, handleClick, selectedStatus, variant = "service" }) => {

    const [expandedStatus, setExpandedStatus] = useState(null);
    const { onOpenSidebar, open, onCloseSidebar, previousPath, currentPath } = useOutletContext();

    console.log("open", open,)

    const getPriorityStyle = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return { bg: '#FFE2E2', color: '#9F0712' };
            case 'medium':
                return { bg: '#FEF9C2', color: '#894B00' };
            default:
                return { bg: '#E3F2FD', color: '#1976D2' };
        }
    };

    // Service Header & Body 
    const renderServiceHeader = (item, bg, color) => (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-serrt"
            px={2}
            pt={2}
            gap={2}
        >
            {/* Left Side */}
            <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                gap={2}
                sx={{ flex: 1, minWidth: 0 }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: '#E7000B',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {item.Id}
                </Typography>

                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                    }}
                >
                    {item.ProjectName} : {item.IssueCategoryName}
                </Typography>


                <Box
                    sx={{
                        display: 'inline-flex',       // shrink/grow to fit text
                        height: '20px',
                        px: 1,                        // add some horizontal padding
                        borderRadius: '6px',
                        border: '1px solid #E5E5E5',
                        fontSize: '12px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        whiteSpace: 'nowrap',         // prevent wrapping
                    }}
                >
                    {item.AllocationLagDays} / {item.LagDays}
                </Box>


                <Chip
                    label={item.Priority}
                    size="small"
                    sx={{
                        width: '80px',
                        height: '20px',
                        borderRadius: '6px',
                        backgroundColor: bg,
                        color,
                        fontSize: '12px',
                    }}
                />
            </Box>

            {/* Right Side */}
            <Button
                variant="contained"
                color="secondary"
                onClick={(e) => handleClick(e, item)}
                sx={{
                    width: '80px',
                    height: '28px',
                    borderRadius: '9px',
                    fontSize: '12px',
                    textTransform: 'none',
                    alignSelf: 'flex-start',
                    fontWeight: 600,
                }}
            >
                Service
            </Button>
        </Box>
    );

    const renderServiceBody = (item) => (
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon fontSize="small" sx={{ color: '#9A9A9A' }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500 }}>
                        Issue Date
                    </Typography>
                </Box>

                <Typography variant="body2" color="text.primary">
                    {item.CreatedDate ? formatDate(item.CreatedDate) : ''}
                </Typography>
            </Grid>
            <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" sx={{ color: '#9A9A9A' }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500 }}>
                        Appointment Date
                    </Typography>
                </Box>

                <Typography variant="body2" color="text.primary">
                    {item.AppointmentDate ? formatDate(item.AppointmentDate) : ''}
                </Typography>
            </Grid>
            <Grid container item xs={12} rowSpacing={0.75}>
                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Client/Site:{' '}
                        <Typography component="span" color="text.secondary">
                            {item.ClientName} : {item.Site}
                        </Typography>
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Owned by: <span style={{ color: '#6b7280' }}>{item.IssueOwnedBy || '-'}</span>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{/* adjust spaces as needed */}
                        Allocated by: <span style={{ color: '#6b7280' }}>{item.LastAllocatedBy || ''}</span>
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Last remark:{' '}
                        <Typography
                            component="span"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                                __html: (item.IssueCategory_LastRemark || "").replace(/\n/g, "<br/>"),
                            }}
                        />
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Issue remark:{' '}
                        <Typography component="span" color="text.secondary" dangerouslySetInnerHTML={{
                            __html: (item.Remarks || "").replace(/\n/g, "<br/>"),
                        }}
                        />

                    </Typography>
                </Grid>
            </Grid>

        </Grid>

    );

    // Edit Service Header & Body 
    const renderEditHeader = (item, bg, color) => (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            px={2}
            pt={2}
            gap={2}
        >
            {/* Left Side */}
            <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                gap={2}
                sx={{ flex: 1, minWidth: 0 }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: '#E7000B',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {item.ServiceId}
                </Typography>

                <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    sx={{
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                    }}
                >
                    {item.ProjectName} : {item.IssueCategory}
                </Typography>


                <Box
                    sx={{
                        display: 'inline-flex',       // shrink/grow to fit text
                        height: '20px',
                        px: 1,                        // add some horizontal padding
                        borderRadius: '6px',
                        border: '1px solid #E5E5E5',
                        fontSize: '10px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        whiteSpace: 'nowrap',
                        fontWeight: 500// prevent wrapping
                    }}
                >
                    {item.AllocationLagDays} / {item.LagDays}
                </Box>

                <Chip
                    label={item.Priority}
                    size="small"
                    sx={{
                        width: '80px',
                        height: '20px',
                        borderRadius: '6px',
                        backgroundColor: bg,
                        color,
                        fontSize: '12px',
                    }}
                />
            </Box>

            {/* Right Side */}
            <Button
                variant="contained"
                color="secondary"
                startIcon={
                    <Icon
                        icon="material-symbols:edit-outline-rounded"
                        width="18"
                        height="18"
                        style={{ color: '#FFFFFF' }}
                    />
                }
                onClick={(e) => handleClick(e, item)}
                sx={{
                    width: '80px',
                    height: '28px',
                    borderRadius: '9px',
                    fontSize: '14px',
                    textTransform: 'none',
                    alignSelf: 'flex-start',
                    fontWeight: 600,
                }}

            >
                Edit
            </Button>
        </Box>
    );

    const renderEditBody = (item) => (
        <Grid container spacing={1}  >
            {/* TOP ROW — 4 FIELDS */}
            <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ConfirmationNumberOutlinedIcon fontSize="small" sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: 10 }}>
                        Issue ID
                    </Typography>
                </Box>
                <Typography variant="body2">{item.IssueId}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonthIcon fontSize="small" sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: 10 }}>
                        Start Date
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.ServiceStartedAt ? formatDate(item.ServiceStartedAt) : ''}
                </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonthIcon fontSize="small" sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: 10 }}>
                        End Date
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.ServiceEndedAt ? formatDate(item.ServiceEndedAt) : ''}
                </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography variant="caption" sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: 10 }}>
                        Appointment Date
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.AppointmentDate ? formatDate(item.AppointmentDate) : ''}
                </Typography>
            </Grid>

            {/* CLIENT / SITE */}
            <Grid container item xs={12} rowSpacing={0.50} mt={1}>
                <Grid item xs={12} >
                    <Typography variant="body2" fontWeight={500}>
                        Client/Site:{' '}
                        <Typography component="span" color="text.secondary">
                            {item.ClientName} : {item.SiteName}
                        </Typography>
                    </Typography>
                </Grid>

                {/* 4 FIELDS — SINGLE ROW */}
                {!open ? (
                    // 🔹 4 FIELDS — SINGLE ROW
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight={500}>
                                Activity:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ActivityName || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight={500}>
                                Service Type:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ServiceType || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight={500}>
                                Project Version:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ProjectVersionName || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight={500}>
                                Issue Sub-status:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.SubStatus || ''}
                                </Typography>
                            </Typography>
                        </Grid>
                    </>
                ) : (
                    // 🔹 2 FIELDS — SINGLE ROW
                    <>
                        <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="body2" fontWeight={500}>
                                Activity:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ActivityName || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="body2" fontWeight={500}>
                                Service Type:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ServiceType || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="body2" fontWeight={500}>
                                Project Version:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.ProjectVersionName || ''}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="body2" fontWeight={500}>
                                Issue Sub-status:{' '}
                                <Typography component="span" color="text.secondary">
                                    {item.SubStatus || ''}
                                </Typography>
                            </Typography>
                        </Grid>
                    </>
                )}

                {/* SERVICE PROVIDED */}
                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Service Provided:{' '}
                        <Typography
                            component="span"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                                __html: (item.ServiceProvided || '').replace(/\n/g, '<br/>'),
                            }}
                        />
                    </Typography>
                </Grid>

                {/* REMARK */}
                <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500}>
                        Remark:{' '}
                        <Typography
                            component="span"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                                __html: (item.IssueReported || '').replace(/\n/g, '<br/>'),
                            }}
                        />
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );

    const renderServiceHistoryBody = (item) => (

        <Grid container spacing={1} alignItems="center">
            {/* DATE */}
            <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonthIcon sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: '10px' }}>
                        Date
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.ServiceStartTime ? formatDate(item.ServiceStartTime) : '-'}
                </Typography>
            </Grid>

            {/* DURATION */}
            <Grid item xs={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: '10px' }}>
                        Duration
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.TotalServiceMinutes || '-'}
                </Typography>
            </Grid>

            {/* EXECUTIVE */}
            <Grid item xs={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ color: '#9A9A9A', fontSize: 14 }} />
                    <Typography sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: '10px' }}>
                        Executive
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {item.ExecutiveName || '-'}
                </Typography>
            </Grid>

            {/* RIGHT ALIGNED ICONS */}
            <Grid item xs={2}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                    }}
                >
                    <FactCheckOutlinedIcon
                        sx={{ fontSize: 20, color: '#2E7D32', cursor: 'pointer' }}
                    />
                    <HistoryToggleOffOutlinedIcon
                        sx={{ fontSize: 20, color: '#1E5DB6', cursor: 'pointer' }}
                    />
                </Box>
            </Grid>

            {/* SERVICE REMARK — FULL WIDTH */}
            <Grid item xs={12} mt={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon
                        icon="material-symbols:list-alt-check-outline-rounded"
                        width={14}
                        height={14}
                        color="#9A9A9A"
                    />
                    <Typography sx={{ color: '#9A9A9A', fontWeight: 500, fontSize: '10px' }}>
                        Service Remark
                    </Typography>
                </Box>

                <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{
                        __html: (item.ServiceProvided || '').replace(/\n/g, '<br/>'),
                    }}
                />
            </Grid>
        </Grid>
    );


    return (
        <Box>
            {(serviceData || [])
                // .filter((item) =>
                //    item.SubStatus?.toLowerCase() === selectedStatus?.toLowerCase()
                // )

                .filter((item) =>
                    variant === "edit" || variant === "history"
                        ? true
                        : item.SubStatus?.toLowerCase() === selectedStatus?.toLowerCase()
                )

                .map((item) => {
                    const { bg, color } = getPriorityStyle(item.Priority);

                    return (
                        <Card key={item.Id} sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #ddd' }}>

                            {variant === "edit" && renderEditHeader(item, bg, color)}
                            {variant === "service" && renderServiceHeader(item, bg, color)}
                            {variant === "history" && null}

                            {variant === "history" && (
                                <Box sx={{ p: 1 }}>
                                    {renderServiceHistoryBody(item)}
                                </Box>
                            )}
                            {(variant === "service" || variant === "edit") && (
                                <CardContent
                                    sx={{ pt: 1, pb: '16px !important' }
                                    }
                                >

                                    {variant === "service" && renderServiceBody(item)}
                                    {variant === "edit" && renderEditBody(item)}
                                    {variant === "history" && renderServiceHistoryBody(item)}

                                </CardContent>
                            )}
                        </Card>
                    );
                })}
        </Box>
    );
};

export default ServiceCards;
